import { defineStore } from 'pinia';
import { computed, ref } from 'vue';

import { exchangeFirebaseToken } from '@/api/apiClient/authSession';
import { apiRequest } from '@/api/apiClient/base';
import { getMe } from '@/api/apiClient/users';
import type { User } from '@/api/types';
import { getFirebaseAuthProvider } from '@/features/auth/firebaseAuthProvider';

/**
 * The acting identity for the SPA. The identity is read on boot from
 * `GET /api/users/me` (the stored-PAT operator or an invite-redeemed session
 * user); a Firebase sign-in additionally establishes it by exchanging an ID
 * token for a session (`signInWithFirebaseToken` → `POST /api/auth/session`).
 * Sign-out clears the server session via `POST /api/logout` and, when a Firebase
 * sign-in established this session, the persisted Firebase client session too.
 */
export const useAuthStore = defineStore('auth', () => {
  const user = ref<User | null>(null);
  // Whether the identity is a session (a cookie the SPA can clear). Gates sign-out.
  const session = ref(false);
  // The session's admin snapshot, from the caller's Firebase claims at sign-in.
  // There is no local role field, so admin-ness is read from the identity
  // response and held here; it refreshes on the next sign-in.
  const isAdmin = ref(false);
  const loaded = ref(false);

  const isAuthenticated = computed(() => user.value !== null);
  const isSession = computed(() => user.value !== null && session.value);
  const displayName = computed(() => user.value?.name || user.value?.login || '');
  // Who may reach the admin-only Settings surface: an admin, or — as a
  // first-run carve-out — the unconfigured operator who has no identity yet.
  // The single source of truth shared by the router's Settings guard and the
  // no-PAT SetupNotice, so neither dead-ends a member at a page they can't open.
  const canAccessSettings = computed(() => isAdmin.value || !isAuthenticated.value);

  async function fetchIdentity(): Promise<void> {
    try {
      // getMe already maps the "no identity" 404 to null; reaching this catch
      // means an unexpected failure (5xx / network) — the shell still renders
      // signed-out, but surface it rather than silently swallowing.
      const identity = await getMe();
      user.value = identity?.user ?? null;
      session.value = identity?.session ?? false;
      isAdmin.value = identity?.isAdmin ?? false;
    } catch (e) {
      console.warn('Failed to load identity', e);
      user.value = null;
      session.value = false;
      isAdmin.value = false;
    } finally {
      loaded.value = true;
    }
  }

  async function signInWithFirebaseToken(idToken: string): Promise<void> {
    // The server verifies the token and returns the canonical identity plus an
    // HttpOnly `nori_session` cookie; reflect it directly. A rejected exchange
    // (401/404) bubbles and leaves the store untouched, since state is only
    // assigned after the awaited exchange resolves.
    const identity = await exchangeFirebaseToken(idToken);
    user.value = identity.user;
    session.value = identity.session;
    isAdmin.value = identity.isAdmin;
    loaded.value = true;
  }

  async function logout(): Promise<void> {
    // Also clear the Firebase client session so an explicit sign-out leaves no
    // persisted, auto-refreshing Firebase identity behind. Best-effort and
    // provider-optional: the `?.` short-circuits when no adapter is registered,
    // and the adapter no-ops (without loading the SDK) when Firebase was unused.
    await getFirebaseAuthProvider()
      ?.signOut()
      .catch(() => undefined);
    await apiRequest('/api/logout', { method: 'POST' }).catch(() => undefined);
    user.value = null;
    session.value = false;
    isAdmin.value = false;
  }

  return {
    user,
    session,
    loaded,
    isAuthenticated,
    isAdmin,
    isSession,
    displayName,
    canAccessSettings,
    fetchIdentity,
    signInWithFirebaseToken,
    logout,
  };
});
